import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useViewport } from '../hooks/useViewport';

interface Props {
  title: string;
  subtitle?: ReactNode;
  /** Show a Back button (App Mode has no browser back). Default false. */
  back?: boolean;
  /** Right-aligned actions (buttons, etc.). */
  actions?: ReactNode;
}

/**
 * Per-page toolbar under the app header: an optional Back button (essential in
 * the installed/standalone window, which has no browser back), the page title,
 * and right-aligned actions. Mirrors the GOS Work-Order-Detail toolbar.
 *
 * ON A PHONE IT STACKS
 * The title and its actions shared one `wrap="nowrap"` row, which is right on a desktop and wrong
 * on a 430px screen: "Purchase orders" beside "New purchase order" leaves each of them squeezed
 * into about half a screen, so the title truncates and the button's label wraps inside itself.
 * Stacking gives the title its full width and the actions their own row, where `grow` spreads them
 * edge to edge — which also makes them a comfortable thumb target rather than a 36px pill in a
 * corner. Nothing changes at tablet width and above.
 */
export function PageHeader({ title, subtitle, back = false, actions }: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const handheld = useViewport() === 'handheld';

  const heading = (
    <Group gap="sm" align="center" wrap="nowrap">
      {back && (
        <Button
          variant="default"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => router.history.back()}
        >
          {t('shell.back')}
        </Button>
      )}
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Title order={2}>{title}</Title>
        {/* A node subtitle renders as a div, not the default <p>: the detail screens pass a
            status badge and a line of text, and a <div> or a second <p> inside a <p> is invalid
            markup the browser silently reshapes. A plain string keeps the paragraph. */}
        {subtitle && (
          <Text size="sm" c="dimmed" component={typeof subtitle === 'string' ? 'p' : 'div'}>
            {subtitle}
          </Text>
        )}
      </Stack>
    </Group>
  );

  if (handheld) {
    return (
      <Stack gap="sm" mb="md">
        {heading}
        {actions && (
          <Group gap="sm" grow wrap="wrap">
            {actions}
          </Group>
        )}
      </Stack>
    );
  }

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" mb="md">
      {heading}
      {actions && (
        <Group gap="sm" wrap="nowrap">
          {actions}
        </Group>
      )}
    </Group>
  );
}
